import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Container, Content, ComboProps } from 'styles/sgo_wrappers';

import SGOHeader from 'components/SGOHeader';
import SGONavbar from 'components/SGONavbar';
import ScrollTop from 'components/ScrollTop';
import SGOFooter from 'components/SGOFooter';
import Loading from 'components/Loading';
import SelectV2 from 'components/SelectV2';
import RadioContainer from 'components/RadioContainer';
import Input from 'components/Input';
import Textarea from 'components/Textarea';

import { useAuth } from 'hooks/auth';
import { useToast } from 'hooks/toast';
import { useCredentials } from 'hooks/credentials';
import { useChanges } from 'hooks/changes';

import api from 'services/api';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { FaDollarSign } from 'react-icons/fa';
import Button from 'components/Button';
import getValidationErrors from 'utils/getValidationErrors';

import * as yup from 'yup';
import { endOfDay, formatDate, minimumAllowedDate } from 'utils/formatDate';
import { useHistory, useLocation } from 'react-router-dom';
import { getLocalStorage, setLocalStorage } from 'utils/handleLocalStorage';
import { format, isValid, subDays } from 'date-fns';
import { CoordIdentifier, Wrapper } from '../insert/styles';
import { ListProps } from '../main';
import { ComboLancProps } from '../insert';

import { LocalStorageProps } from '../main';

interface StateProps {
  reg: ListProps;
}

interface FormData {
  seq: string;
  movdate: string;
  movtype: string;
  compl: string;
  lanc: string;
  value: string;
}

const AplicUpdate: React.FC = () => {
  const location = useLocation<StateProps>();
  const history = useHistory();
  const { errorHandling } = useCredentials();
  const { addToast } = useToast();
  const { currDate } = useChanges();
  const { user } = useAuth();

  const [registro] = useState(() => {
    if (!location.state) {
      return {} as ListProps;
    }
    return location.state.reg;
  });

  const formRef = useRef<FormHandles>(null);
  const [loading, setLoading] = useState(false);
  const [rawLancs, setRawLancs] = useState<ComboLancProps[]>([]);
  const [lancs, setLancs] = useState<ComboProps[]>([]);
  const [lancDesc, setLancDesc] = useState(() => {
    return location.state.reg.lanc;
  });

  const [initRadio] = useState(() => {
    if (!location.state) {
      return 'E';
    }
    return location.state.reg.type;
  });

  const getComboTLanc = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('combos/comboTipoLanc.php');

      setRawLancs(response.data);

      setLancs(
        response.data
          .filter((item: ComboLancProps) => item.type === initRadio)
          .map((item: ComboLancProps) => ({
            value: item.code,
            label: item.desc,
          })),
      );
      setLoading(false);
    } catch (err) {
      errorHandling(err);
    }
  }, [errorHandling, initRadio]);

  useEffect(() => {
    if (!location.state) {
      history.replace(location.pathname.replace('/update', ''));
      return;
    }
    getComboTLanc();
  }, [getComboTLanc, history, location.pathname, location.state]);

  const handleRadioClick = useCallback(() => {
    const val = formRef.current?.getFieldValue('movtype');

    formRef.current?.setFieldValue(
      'lanc',
      val === registro.type ? registro.lanc : '',
    );

    setLancs(
      rawLancs
        .filter((item: ComboLancProps) => item.type === val)
        .map((item: ComboLancProps) => ({
          value: item.code,
          label: item.desc,
        })),
    );

    setLancDesc(val === registro.type ? registro.landesc : 'Selecione');
  }, [rawLancs, registro.lanc, registro.landesc, registro.type]);

  const handleSubmit = useCallback(
    async (data: FormData) => {
      try {
        setLoading(true);
        formRef.current?.setErrors({});

        const { full } = currDate;

        const max = endOfDay(full);

        if (!isValid(new Date(data.movdate))) {
          formRef.current?.setFieldError(
            'movdate',
            'Este campo é obrigatório.',
          );
          setLoading(false);
          return;
        }

        const min = minimumAllowedDate(data.movdate);

        if (parseFloat(data.value) <= 0) {
          formRef.current?.setFieldError(
            'value',
            'O valor deve ser superior a 0 (zero).',
          );

          formRef.current?.setFieldValue('value', '0.00');
          setLoading(false);
          return;
        }

        const schema = yup.object().shape({
          movdate: yup
            .date()
            .typeError('Data inválida.')
            .min(
              min,
              `O lançamento deve ser posterior a ${formatDate(
                subDays(min, 1).toISOString(),
              )}`,
            )
            .max(
              max,
              `A data do lançamento deve ser igual ou anterior à data atual (${format(
                max,
                'dd/MM/yyyy',
              )}).`,
            ),
          movtype: yup.string().required(),
          compl: yup
            .string()
            .trim()
            .min(5, 'Quantidade mínima de caracteres não atingida (5).')
            .max(60, 'Quantidade máxima de caracteres excedida (60).'),
          lanc: yup.string().notOneOf(['Selecione', '']),
          value: yup
            .string()
            .notOneOf(['0.00', ''], 'Este campo é obrigatório.')
            .required(),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        const send = new FormData();
        send.append(
          'data',
          JSON.stringify({
            ...data,
            lanc: data.lanc === registro.landesc ? registro.lanc : data.lanc,
          }),
        );

        await api.post('/sgo/aplic_update.php', send, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        addToast({
          type: 'success',
          title: 'Sucesso!',
          description: 'Registro alterado.',
        });
        setLocalStorage(`${process.env.REACT_APP_FPG}`, {
          ano: new Date(data.movdate).getUTCFullYear(),
          mes: new Date(data.movdate).getUTCMonth() + 1,
        });
        setLoading(false);

        const { app }: LocalStorageProps = getLocalStorage(
          `${process.env.REACT_APP_APLIC}`,
        );

        history.replace(location.pathname.replace('/update', `/${app}`));
      } catch (err) {
        setLoading(false);
        if (err instanceof yup.ValidationError) {
          const errors = getValidationErrors(err);

          formRef.current?.setErrors(errors);
          return;
        }
        errorHandling(err);
      }
    },
    [
      addToast,
      currDate,
      errorHandling,
      history,
      location.pathname,
      registro.lanc,
      registro.landesc,
    ],
  );

  return (
    <Container>
      <Loading isLoading={loading} />
      <ScrollTop />
      <SGOHeader />
      <SGONavbar noLinks title="Editar Lançamento" />
      <Content>
        <Form
          ref={formRef}
          onSubmit={handleSubmit}
          initialData={{
            seq: registro.seq,
            movdate: registro.date,
            movtype: initRadio,
            lanc: registro.lanc,
            compl: registro.compl,
            value: registro.value.toFixed(2),
          }}
        >
          <CoordIdentifier>
            {user.perfil === 'ZON' ? user.zondesc : user.anbdesc}
          </CoordIdentifier>
          <Wrapper>
            <Input isHidden name="seq" />
            <span>
              <p>Data</p>
              <Input name="movdate" placeholder="dd/mm/aaaa" type="date" />
            </span>
            <span>
              <p>Tipo</p>
              <RadioContainer
                title=" "
                onClick={handleRadioClick}
                name="movtype"
                content={[
                  {
                    id: 'mE',
                    value: 'E',
                    label: 'Entrada',
                  },
                  {
                    id: 'mS',
                    value: 'S',
                    label: 'Saída',
                  },
                ]}
                containerStyle={{ alignItems: 'flex-start' }}
                itemsStyle={{ justifyContent: 'flex-start' }}
                selected={initRadio}
              />
            </span>
            <span>
              <p>Descrição:</p>
              <SelectV2
                name="lanc"
                content={lancs}
                initial={lancDesc || 'Selecione'}
              />
            </span>
            <span>
              <p>Complemento</p>
              <Textarea
                name="compl"
                placeholder="Insira o complemento deste lançamento"
                mask="alpha"
              />
            </span>
            <span>
              <p>Valor</p>
              <Input
                name="value"
                type="number"
                step=".01"
                icon={FaDollarSign}
                placeholder="0,00"
                mask="currency"
              />
            </span>
          </Wrapper>
          <Button
            bgcolor="#00802b"
            type="submit"
            containerStyle={{ marginBottom: '10px' }}
          >
            Lançar
          </Button>
        </Form>
      </Content>
      <SGOFooter />
    </Container>
  );
};

export default AplicUpdate;
